import React, { useEffect, useMemo, forwardRef } from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import * as THREE from 'three'

const MODEL_BASE_URL = process.env.REACT_APP_MODEL_STORAGE_CDN || '/';

// All props correctly destructured here!
export const ToyotaTundra = forwardRef(({ customColors, currentBumper, showCanopy, showRack, showFlares, showGrill, showWinch, isMobile, onPartClick, ...props }, ref) => {
  const model_path = `${MODEL_BASE_URL}toyota_tundra_fully_loaded_optimized_mobile.glb`;
  const MODEL_URL = isMobile 
    ? model_path   
    : model_path;

  const { scene, materials } = useGLTF(MODEL_URL)
  const clonedScene = useMemo(() => scene.clone(), [scene])

  const textures = useTexture({
    blinker: '/models/toyota_tundra/textures/Blinker_baseColor.jpeg',
    headlight: '/models/toyota_tundra/textures/Headlight_baseColor.png',
    headlight001: '/models/toyota_tundra/textures/Headlight.001_baseColor.png',
    light: '/models/toyota_tundra/textures/Light_baseColor.png',
    roofBrake: '/models/toyota_tundra/textures/Roof_Brake_baseColor.jpeg',
    tailLight: '/models/toyota_tundra/textures/Tail_Light_Interior_baseColor.png',
    tireText: '/models/toyota_tundra/textures/Tire_Text_baseColor.png',
    windowDefroster: '/models/toyota_tundra/textures/Window_Defroster_baseColor.png'
  });

 // 1. MATERIAL & TEXTURE INJECTION
  useEffect(() => {
    Object.values(textures).forEach(tex => {
      tex.flipY = false;
      tex.colorSpace = THREE.SRGBColorSpace;
    });

    clonedScene.traverse((child) => {
      if (child.isMesh) {
        const mat = child.material;
        const name = child.name.toLowerCase();

        // LOGO/TEXT COLORS
        // This targets 'TUNDRA_Text_Left_Door', 'TUNDRA_Text_Left_Door001', etc.
        // It forces them to use the Silver/Chrome material instead of body paint.
        if (name.includes('tundra_text')) {
          child.material = materials.Silver || materials.Chrome || mat;
        }

        // --- i-FORCE FENDER LOGOS ---
        if (name.includes('force_logo')) {
          mat.transparent = true;
          mat.alphaTest = 0.5; 
          mat.color.set('#ffffff');
          mat.metalness = 1.0;
          mat.roughness = 0.1;
        }

        // FIX WHITE DOOR
        if (!name.includes('tundra_text') && ((name.includes('door') && name.includes('left')) || name.includes('rear_door'))) {
           child.material = materials.Truck_Color || mat; 
        }

        // TEXTURES
        if (mat.name === 'Blinker') { mat.map = textures.blinker; mat.emissiveMap = textures.blinker; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.2; }
        if (mat.name === 'Headlight') { mat.map = textures.headlight; mat.emissiveMap = textures.headlight; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.0; }
        if (mat.name === 'Headlight.001') { mat.map = textures.headlight001; mat.emissiveMap = textures.headlight001; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.0; }
        if (mat.name === 'Light') { mat.map = textures.light; mat.emissiveMap = textures.light; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.0; }
        if (mat.name === 'Roof_Brake') { mat.map = textures.roofBrake; mat.emissiveMap = textures.roofBrake; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.0; }
        if (mat.name === 'Tail_Light_Interior') { mat.map = textures.tailLight; mat.emissiveMap = textures.tailLight; mat.color.set('#ffffff'); mat.emissive.set('#ffffff'); mat.emissiveIntensity = 1.0; }
        if (mat.name === 'Tire_Text') { mat.map = textures.tireText; mat.transparent = true; mat.color.set('#ffffff'); }

        // WINDOW DEFROSTER / WINDSHIELD LABEL
        if (mat.name === 'Window_Defroster') {
          mat.map = textures.windowDefroster;
          mat.transparent = true;
          mat.opacity = 0.6;
          mat.color.set('#ffffff');
          mat.depthWrite = false;
        }

        // GLASS & PLASTICS
        if (mat.name.toLowerCase().includes('glass')) {
          mat.transparent = true; mat.opacity = 0.2; mat.color.set('#ffffff'); mat.roughness = 0.0; mat.metalness = 0.2; mat.envMapIntensity = 1.0;
          mat.depthWrite = false; // Fix artifacts for overlapping glass
        }

        // SPECIFIC FIX FOR WINDSHIELD LABEL ARTIFACTS
        if (name.includes('low_res')) {
          mat.transparent = true;
          mat.opacity = 0.4; // Slightly more visible if it's a label
          mat.depthWrite = false;
          mat.polygonOffset = true;
          mat.polygonOffsetFactor = -1; // Pull it in front of the main glass
        }

        if (mat.name.includes('Red_Tail_Light')) {
          mat.transparent = true; mat.opacity = 0.6; mat.color.set('#aa0000'); mat.emissive.set('#000000'); mat.roughness = 0.1; mat.metalness = 0.3;
        }
        if (['Rubber', 'Flat_Black', 'Black_Plastics_Mesh', 'Black_Plastic', 'material_0'].includes(mat.name)) {
          mat.color.set('#111111'); mat.roughness = 0.9; mat.metalness = 0.0;
        }
        if (['Chrome', 'Silver'].includes(mat.name)) {
          mat.color.set('#ffffff'); 
          mat.roughness = 0.1; 
          mat.metalness = 1.0; 
          mat.envMapIntensity = 1.0;
        }

        mat.needsUpdate = true;
      }
    });
  }, [clonedScene, textures, materials]);

// 2. TOGGLE ACCESSORIES & BUMPERS
useEffect(() => {
  // HELPER: Safely force parent folders open WITHOUT resurrecting hidden parts!
  const unlockParents = (childNode) => {
    let p = childNode.parent;
    while (p) {
      const pName = p.name.toLowerCase();
      if (!pName.includes('license_plate')) {
        p.visible = true;
      }
      p = p.parent;
    }
  };

  clonedScene.traverse((child) => {
    if (child.isObject3D) {
      const exactName = child.name;
      const lowerName = child.name.toLowerCase();

      // 0. PERMANENTLY HIDDEN PARTS
      const permanentlyHidden = ['license_plate'];
      if (permanentlyHidden.some(part => lowerName.includes(part))) {
        child.visible = false;
        return; 
      }

      // 0.5. PERMANENTLY VISIBLE PARTS
      const alwaysOnParts = ['spring', 'id4947_1', 'hitch', 'steering'];
      if (alwaysOnParts.some(part => lowerName.includes(part))) {
        child.visible = true;
        unlockParents(child);
        return; 
      }

      // 1. CANOPY & FLARES
      if (lowerName.includes('canopy') || lowerName.includes('shell') || lowerName.includes('camper')) {
        child.visible = showCanopy;
        return; 
      }
      if (lowerName.includes('flare') || lowerName.includes('fender_flare') || lowerName.includes('fender')) {
        child.visible = showFlares;
        return; 
      }

      // 2. ROOF RACK
      if ((lowerName.includes('rack') || lowerName.includes('roof_rack') || lowerName.includes('lightbar')) && !lowerName.includes('grill') && !lowerName.includes('chrome')) {
        child.visible = showRack;
        return; 
      }

      // 2.5 WINCH TOGGLE 
      const winchParts = ['winch', 'winch_cylinder1', 'cylinder014', 'cube007'];
      if (winchParts.some(part => lowerName.includes(part))) {
        child.visible = showWinch;
        if (showWinch) unlockParents(child);
        return; 
      }

      // 3. STRICT GLTF BUMPERS
      const offroadParts = [
        'grill_guard',      
        'id4434',           
        'rear_bumper001',   
        'sweep004',         
        'sweep005',         
        'sweep1',           
        'id4231',           
        'fog_light',        
        'ring',             
        'light_box',        
        'bush_guard',       
        'cube008',          
        'light1', 'light005', 'light007', 'light008',
        'light013', 'light014', 'light015', 'light016',
      ];
      
      const stockParts = [
        'front_bumper',     
        'id3056',           
        'bumper_plastic'    
      ];

      // 3.1 SPECIFIC BUMPER SCREWS (to avoid capturing flare screws)
      const bumperScrews = ['screw02', 'screw001', 'screw002', 'screw003'];
      if (bumperScrews.includes(lowerName) || exactName === 'Screw') {
        const isVisible = currentBumper === 'offroad';
        child.visible = isVisible;
        if (isVisible) unlockParents(child);
        return;
      }

      // --- THE "BOUNCER" LOGIC ---
      if (offroadParts.some(part => lowerName.includes(part))) {
        const isVisible = currentBumper === 'offroad';
        child.visible = isVisible;
        if (isVisible) unlockParents(child);
        return; 
      }
      
      if (stockParts.some(part => lowerName.includes(part))) {
        const isVisible = currentBumper === 'stock';
        child.visible = isVisible;
        if (isVisible) unlockParents(child);
        return; 
      }

      // 4. GENERIC SCREW TOGGLE (Captures remaining screws like Flare screws)
      if (lowerName.includes('screw')) {
        child.visible = showFlares;
        return;
      }
      
      
      // 5. PHYSICAL GRILL SWAP (Surgical glTF-Proof Version)
      
      // Target Upgraded Parts (safely catching the new ID4004002)
      const isUpgradedGrillPart = 
        exactName === 'Chrome_Grill_1' || 
        exactName === 'Chrome_Grill.001' || 
        exactName === 'Chrome_Grill_001' || 
        lowerName.includes('grill_bracket') || 
        lowerName.includes('grill_mesh') || 
        lowerName.includes('id4022') ||
        lowerName.includes('id4004002');

      // Handle the floating Toyota Logos safely
      if (lowerName.includes('toyota_logo')) {
        if (exactName.includes('.001') || exactName.includes('_1')) {
          child.visible = showGrill;
        } else {
          child.visible = !showGrill;
        }
        return;
      }

      // Apply visibility to Upgraded Grill
      if (isUpgradedGrillPart) {
        child.visible = showGrill;
        if (showGrill) unlockParents(child);
        
        // If the exporter hid the actual geometry inside a generic 'Mesh' folder, toggle it too
        if (child.children) {
          child.children.forEach(c => {
             if (c.name && c.name.toLowerCase() === 'mesh') {
                 c.visible = showGrill;
             }
          });
        }
        return;
      }

      // Apply visibility to Standard Grill precisely
      if (exactName === 'Chrome_Grill') {
        // CRITICAL: Always keep this parent folder open so the off-road bumper guards don't vanish!
        child.visible = true; 

        // Loop through the items inside Chrome_Grill. 
        // Hide the grill geometry, but explicitly IGNORE the bumper guards!
        if (child.children) {
          child.children.forEach(c => {
            if (c.name) {
              const subName = c.name.toLowerCase();
              if (!subName.includes('guard') && !subName.includes('null')) {
                c.visible = !showGrill;
              }
            }
          });
        }
        
        if (!showGrill) unlockParents(child);
        return;
      }
    }
  });
}, [clonedScene, currentBumper, showCanopy, showRack, showFlares, showGrill, showWinch, materials]);

  // 3. COLOR CUSTOMIZATION
  useEffect(() => {
  clonedScene.traverse((child) => {
    if (child.isMesh && child.material) {
      const matName = child.material.name.toLowerCase();

      // 1. Target the Body Paint
      if (matName.includes('truck_color')) {
        child.material.color.set(customColors.main);
         // --- PEARLESCENT / METALLIC REFINEMENT ---
      // Metalness creates the "flake" reflection (0.6 - 0.8 is sweet spot)
        child.material.metalness = 0.7;
        
      // Roughness controls how "blurred" the reflections are. 
      // Lower = shinier. 0.2 is a smooth automotive finish.
        child.material.roughness = 0.2; 
        // Clearcoat adds a secondary reflective layer on top of the paint
        child.material.clearcoat = 1.0; 

        // ClearcoatRoughness makes the "top coat" look perfectly smooth
        child.material.clearcoatRoughness = 0.05;

      // EnvMapIntensity controls how much the sky/environment reflects off the paint
        child.material.envMapIntensity = 1.0; 
        child.material.needsUpdate = true;
      }

      // 2. Target the Wheels
      if (matName.includes('wheel_color')) {
        child.material.color.set(customColors.wheels);
        child.material.metalness = 0.8; 
        child.material.roughness = 0.35; 
        child.material.envMapIntensity = 1.0;
        child.material.needsUpdate = true;
      }
    }
  });
}, [customColors, clonedScene]);

  return (
    <group 
      ref={ref}
      {...props} 
      dispose={null}
      onClick={(e) => {
        e.stopPropagation();
        if (onPartClick) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Clicked Mesh: ${e.object.name}`);
          }
          onPartClick(e.object.name);
        }
      }}
    >
      <primitive object={clonedScene} />
    </group>
  )
});

useGLTF.preload(`${MODEL_BASE_URL}toyota_tundra_fully_loaded_optimized_mobile.glb`)
